/**
 * Filter Dialogs Component
 * Reusable filter dialogs for Location, Item, and Vendor
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  InputAdornment, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, Typography, Box, useTheme, alpha
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';

export default function FilterDialogs({
  // Location filter
  locationFilterDialogOpen,
  setLocationFilterDialogOpen,
  locationSearchTerm = '',
  setLocationSearchTerm,
  locations = [],
  dialogSelectedLocation,
  setDialogSelectedLocation,
  onApplyLocationFilter,

  // Item filter
  itemFilterDialogOpen,
  setItemFilterDialogOpen,
  itemSearchTerm = '',
  setItemSearchTerm,
  items = [],
  dialogSelectedItem,
  setDialogSelectedItem,
  onApplyItemFilter,
  itemLabel = 'Items',

  // Vendor filter
  vendorFilterDialogOpen,
  setVendorFilterDialogOpen,
  vendorSearchTerm = '',
  setVendorSearchTerm,
  suppliers = [],
  dialogSelectedVendor,
  setDialogSelectedVendor,
  onApplyVendorFilter
}) {
  const theme = useTheme();

  return (
    <>
      {/* Location Filter Dialog */}
      <Dialog
        open={locationFilterDialogOpen}
        onClose={() => setLocationFilterDialogOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '80vh',
            maxHeight: '80vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Filter by Location
          </Typography>
          <IconButton onClick={() => setLocationFilterDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search locations..."
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' }
                }
              }}
            />
          </Box>
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2,
                    pl: 3,
                    pr: 3
                  }
                }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        All Locations
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show inventory for all locations
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDialogSelectedLocation('')}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2,
                        py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: dialogSelectedLocation === '' ? 'black' : 'white',
                        color: dialogSelectedLocation === '' ? 'white' : 'text.primary',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                          bgcolor: dialogSelectedLocation === '' ? 'black' : 'white',
                          color: dialogSelectedLocation === '' ? 'white' : 'text.primary'
                        }
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
                {locations.filter(l => l.name.toLowerCase().includes(locationSearchTerm.toLowerCase())).map(loc => (
                  <TableRow key={loc.id} hover sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2,
                      pl: 3,
                      pr: 3
                    }
                  }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {loc.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDialogSelectedLocation(loc.id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedLocation === loc.id ? 'black' : 'white',
                          color: dialogSelectedLocation === loc.id ? 'white' : 'text.primary',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: dialogSelectedLocation === loc.id ? 'black' : 'white',
                            color: dialogSelectedLocation === loc.id ? 'white' : 'text.primary'
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          background: alpha(theme.palette.grey[50], 0.5)
        }}>
          <Button onClick={() => setLocationFilterDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onApplyLocationFilter} sx={{
            borderRadius: 2,
            px: 3,
            bgcolor: 'black',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
          }}>
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Filter Dialog */}
      <Dialog
        open={itemFilterDialogOpen}
        onClose={() => setItemFilterDialogOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '80vh',
            maxHeight: '80vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Filter by {itemLabel}
          </Typography>
          <IconButton onClick={() => setItemFilterDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder={`Search ${itemLabel.toLowerCase()}...`}
              value={itemSearchTerm}
              onChange={(e) => setItemSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' }
                }
              }}
            />
          </Box>
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>{itemLabel}</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2,
                    pl: 3,
                    pr: 3
                  }
                }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        All {itemLabel}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDialogSelectedItem('')}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2,
                        py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: dialogSelectedItem === '' ? 'black' : 'white',
                        color: dialogSelectedItem === '' ? 'white' : 'text.primary',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                          bgcolor: dialogSelectedItem === '' ? 'black' : 'white',
                          color: dialogSelectedItem === '' ? 'white' : 'text.primary'
                        }
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
                {items.filter(item =>
                  item.item_name?.toLowerCase().includes(itemSearchTerm.toLowerCase())
                ).map(item => (
                  <TableRow key={item.item_id} hover sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2,
                      pl: 3,
                      pr: 3
                    }
                  }}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {item.item_name}
                        </Typography>
                        {item.item_sku && (
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.item_sku}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDialogSelectedItem(item.item_id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedItem === item.item_id ? 'black' : 'white',
                          color: dialogSelectedItem === item.item_id ? 'white' : 'text.primary',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: dialogSelectedItem === item.item_id ? 'black' : 'white',
                            color: dialogSelectedItem === item.item_id ? 'white' : 'text.primary'
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          background: alpha(theme.palette.grey[50], 0.5)
        }}>
          <Button onClick={() => setItemFilterDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onApplyItemFilter} sx={{
            borderRadius: 2,
            px: 3,
            bgcolor: 'black',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
          }}>
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Filter Dialog */}
      <Dialog
        open={vendorFilterDialogOpen}
        onClose={() => setVendorFilterDialogOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '80vh',
            maxHeight: '80vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Filter by Vendor
          </Typography>
          <IconButton onClick={() => setVendorFilterDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search vendors..."
              value={vendorSearchTerm}
              onChange={(e) => setVendorSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' }
                }
              }}
            />
          </Box>
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2,
                    pl: 3,
                    pr: 3
                  }
                }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        All Vendors
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDialogSelectedVendor('')}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2,
                        py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: dialogSelectedVendor === '' ? 'black' : 'white',
                        color: dialogSelectedVendor === '' ? 'white' : 'text.primary',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                          bgcolor: dialogSelectedVendor === '' ? 'black' : 'white',
                          color: dialogSelectedVendor === '' ? 'white' : 'text.primary'
                        }
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
                {suppliers.filter(s =>
                  s.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
                ).map(supp => (
                  <TableRow key={supp.id} hover sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2,
                      pl: 3,
                      pr: 3
                    }
                  }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {supp.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDialogSelectedVendor(supp.id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedVendor === supp.id ? 'black' : 'white',
                          color: dialogSelectedVendor === supp.id ? 'white' : 'text.primary',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: dialogSelectedVendor === supp.id ? 'black' : 'white',
                            color: dialogSelectedVendor === supp.id ? 'white' : 'text.primary'
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          background: alpha(theme.palette.grey[50], 0.5)
        }}>
          <Button onClick={() => setVendorFilterDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onApplyVendorFilter} sx={{
            borderRadius: 2,
            px: 3,
            bgcolor: 'black',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
          }}>
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
